<template>
    <v-row
        no-gutters
    >
        <AdminVoucherDialog
            v-if="adminVoucherDialog.show"
            :show-dialog="adminVoucherDialog.show"
            :discount-id="adminVoucherDialog.id"
            @updated="voucherChanged"
            @deleted="voucherDeleted"
            @close="closeEdit"
        ></AdminVoucherDialog>
        <v-col>
            <v-row no-gutters>
                <v-col class="mb-2">
                    <ModernFilterBar
                        :filter-controller="filterController"
                    />
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <v-toolbar
                        flat
                        class="medium-toolbar"
                        color="transparent"
                    >
                        <v-toolbar-title class="font-weight-bold mb-3">
                            {{ $t("Total items:") }} {{ richDataTableController.getTotalRows() | format_number }}
                        </v-toolbar-title>
                    </v-toolbar>
                </v-col>
                <v-col class="text-right">
                    <v-btn
                        depressed
                        color="primary"
                        class="inverted--text"
                        @click="openEdit(0)"
                    >
                        <v-icon>add</v-icon>
                        {{ $t("Add new") }}
                    </v-btn>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <RichDataTable
                        :rich-data-table-controller="richDataTableController"
                    />
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import {onMounted, reactive, ref} from "@vue/composition-api"
import {$t} from "@/i18n"
import SubscriptionModule from "../../store/modules/subscription"
import AdminVoucherDialog from "@/views/Admin/AdminVoucherDialog"
import {formatNumber, formatDate} from "@/plugins/formatting"
import ModernFilterBar from "@/components/ModernFilterBar/index.vue";
import RichDataTable from "@/components/RichDataTable/RichDataTable.vue";
import UrlAdapter from "@/urlAdapter/urlAdapter";
import FilterBarController from "@/components/ModernFilterBar/FilterBarController";
import RichDataTableController from "@/components/RichDataTable/RichDataTableController";
import {deepClone} from "@/helpers/helpers";

export default {
    name: "AdminVoucher",
    components: {
        RichDataTable,
        ModernFilterBar,
        AdminVoucherDialog
    },
    setup() {
        const urlAdapter = new UrlAdapter()

        const {adminFetchVouchers} = SubscriptionModule()
        const adminVoucherDialog = ref({show: false, id: 0})

        /** @type {FilterBarController} */
        const filterController = reactive(FilterBarController.init(urlAdapter))

        /** @type {RichDataTableController} */
        const richDataTableController = reactive(RichDataTableController.initAsync(urlAdapter))
        const options = [
            {key: "sortBy", default_value: "usage_limit"},
            {key: "sortDesc", default_value: false},
            {key: "itemsPerPage", default_value: 50},
            {key: "page", default_value: 1}
        ]
        richDataTableController.setDefaultOptions(options)

        const filters = [
            {key: "voucher_search", default_value: [''], default: true},
        ]

        const formatCode = (item, col) => {
            if (item[col] == null) return ""
            return item[col]
        }
        const formatPeriodMonths = (item, col) => {
            if (item[col] == null) return ""
            return formatNumber(item[col])
        }
        const formatValidUntil = (item, col) => {
            if (item[col] == null) return ""
            return formatDate(item[col])
        }
        const formatAmount = (item, col) => {
            if (item[col] == null) return ""
            switch (item["type"]) {
                case "sum":
                    return "{0} \u20ac".format(formatNumber(item[col], 2, 2))
                case "percent":
                    return "{0} %".format(formatNumber(item[col], 2, 2))
            }
        }
        const formatAccountPeriod = (item, col) => {
            if (item[col] == null) return ""
            return "{0} - {1}".format(
                formatDate(item["account_created_from"]), formatDate(item["account_created_until"])
            )
        }
        const formatLimit = (item, col) => {
            if (item[col] == null) return ""
            return "{0} / {1}".format(
                formatNumber(item["used_count"]), formatNumber(item["usage_limit"])
            )
        }

        const headers = ref([
            {text: $t("Code"), value: "code", format: formatCode},
            {text: $t("Amount"), value: "amount", format: formatAmount},
            {text: $t("Period"), value: "period_months", format: formatPeriodMonths},
            {text: $t("Account created range"), value: "account_created_from", format: formatAccountPeriod},
            {text: $t("Usage limit"), value: "usage_limit", format: formatLimit},
            {text: $t("Valid until"), value: "valid_until", format: formatValidUntil},
        ])
        const backendQuery = async () => {
            let params = {...filterController.getCurrentFilterValues(), ...richDataTableController.getOptions()}

            const resp = await adminFetchVouchers(params)
            richDataTableController.setRows(resp.items, resp.total_items)
        }

        const openEdit = id => {
            adminVoucherDialog.value.id = id
            adminVoucherDialog.value.show = true
        }
        const closeEdit = () => {
            adminVoucherDialog.value.show = false
            adminVoucherDialog.value.id = 0
        }
        const voucherChanged = (item) => {
            const rows = deepClone(richDataTableController.getRows())

            const index = rows.findIndex(({id}) => id === item.id);
            if (index >= 0) {
                rows[index] = item
            } else {
                rows.push(item)
            }

            richDataTableController.setRows(rows, richDataTableController.getTotalRows())

            closeEdit()
        }
        const voucherDeleted = (itemId) => {
            richDataTableController.setRows(
                richDataTableController.getRows().filter(x => x.id !== itemId),
                richDataTableController.getTotalRows()
            )
            closeEdit()
        }

        richDataTableController.setOnRowClick((item) => {
            openEdit(item.id)
        })

        onMounted(async () => {
            filterController.initFilters(filters)

            filterController.startUrlWatcher()

            richDataTableController.setAllHeaders(headers.value)
            richDataTableController.setVisibleHeaders(headers.value.map(x => x.value))

            await filterController.persistCurrentFilterStateToUrl()
            richDataTableController.setLoadDataFunc(backendQuery)
            await richDataTableController.loadDataFunc()
        })

        return {
            headers,
            adminVoucherDialog,
            backendQuery,
            openEdit,
            closeEdit,
            voucherChanged,
            voucherDeleted,
            filterController,
            richDataTableController
        }
    }
}
</script>
